/** @flow */

// Colour manipulation helpers. Inputs accept "rgb(r, g, b)", "rgba(r, g, b, a)",
// "#rgb" and "#rrggbb"; alpha is ignored — the returned colour is always opaque.

const FALLBACK_COLOR = "#000";

export function parseColor(color: string): Array<number> | null {
  if (typeof color !== "string") {
    return null;
  }

  const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch;
    return [Number(r), Number(g), Number(b)];
  }

  const hexMatch = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    const hex = hexMatch[1];
    const full =
      hex.length === 3
        ? hex
            .split("")
            .map(char => char + char)
            .join("")
        : hex;
    return [
      parseInt(full.slice(0, 2), 16),
      parseInt(full.slice(2, 4), 16),
      parseInt(full.slice(4, 6), 16),
    ];
  }

  return null;
}

// Converts rgb channels (0-255) to [hue, saturation, lightness], all normalised to 0-1.
export function rgbToHsl(r: number, g: number, b: number): Array<number> {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;

  if (max === min) {
    return [0, 0, lightness];
  }

  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let hue;
  if (max === red) {
    hue = (green - blue) / delta + (green < blue ? 6 : 0);
  } else if (max === green) {
    hue = (blue - red) / delta + 2;
  } else {
    hue = (red - green) / delta + 4;
  }

  return [hue / 6, saturation, lightness];
}

function hueToRgbChannel(p, q, t) {
  let temp = t;
  if (temp < 0) temp += 1;
  if (temp > 1) temp -= 1;
  if (temp < 1 / 6) return p + (q - p) * 6 * temp;
  if (temp < 1 / 2) return q;
  if (temp < 2 / 3) return p + (q - p) * (2 / 3 - temp) * 6;
  return p;
}

// Converts [hue, saturation, lightness] (0-1) back to rgb channels (0-255).
export function hslToRgb(h: number, s: number, l: number): Array<number> {
  if (s === 0) {
    const value = Math.round(l * 255);
    return [value, value, value];
  }

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return [
    Math.round(hueToRgbChannel(p, q, h + 1 / 3) * 255),
    Math.round(hueToRgbChannel(p, q, h) * 255),
    Math.round(hueToRgbChannel(p, q, h - 1 / 3) * 255),
  ];
}

// In case we need negative color
export function toNegativeColor(color: string): string {
  const rgb = parseColor(color);
  if (!rgb) {
    return FALLBACK_COLOR;
  }

  return `rgb(${255 - rgb[0]}, ${255 - rgb[1]}, ${255 - rgb[2]})`;
}

// Returns `color` darkened by `percent` (0–100) — hue and saturation are kept,
// only lightness drops: 100% is black, 0% returns the colour unchanged.
export function toDarkerColor(color: string, percent?: number = 10): string {
  const rgb = parseColor(color);
  if (!rgb) {
    return FALLBACK_COLOR;
  }

  const ratio = Math.min(Math.max(Number(percent) || 0, 0), 100) / 100;
  const [hue, saturation, lightness] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const [r, g, b] = hslToRgb(hue, saturation, lightness * (1 - ratio));

  return `rgb(${r}, ${g}, ${b})`;
}
